import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { isToday } from "../utils/dateUtils";
import { io } from "socket.io-client";

import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../firebase-messaging";

import {
  isUrgent,
  isUrgentToday
} from "../utils/urgenceUtils";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid
} from "recharts";

const getArrivalStatus = () => {
  const now = new Date();
  const hour = now.getHours();

  if (hour >= 22) {
    return { label: "Arrivée critique", color: "text-red-600" };
  }

  if (hour >= 18) {
    return { label: "Arrivée attendue", color: "text-orange-500" };
  }

  return { label: "En attente", color: "text-gray-500" };
};

const getDepartureStatus = (dateFinPrevue) => {
  if (!dateFinPrevue) return "";

  const now = new Date();

  const departureDate = new Date(dateFinPrevue);
  departureDate.setHours(12, 0, 0, 0);

  const diff = departureDate - now;

  if (diff <= 0) {
    return "Expiré ⚠️";
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));

  const minutes = Math.floor(
    (diff % (1000 * 60 * 60)) / (1000 * 60)
  );

  return `Départ dans ${hours}h${minutes
    .toString()
    .padStart(2, "0")}`;
};

export default function DashboardReception() {
  
  const [data, setData] = useState([]);

  const [reservationsWeb, setReservationsWeb] = useState([]);
  
  const [rembCount, setRembCount] = useState(0);

  const [notificationsEnabled, setNotificationsEnabled] =
    useState(false);

  const navigate = useNavigate();

  const token = localStorage.getItem("accessToken");

  const handleClick = (appartement) => {
    if (!appartement) return;

    localStorage.setItem(
      "selectedAppartement",
      JSON.stringify(appartement)
    );

    navigate("/appartement");
  };

  const loadData = async () => {
    const res = await api.get("/appartements");
    setData(res.data);
  };

  const loadRemboursements = async () => {
    try {
      const res = await api.get(
        "/sejours/remboursements/count"
      );

      setRembCount(res.data.total);

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let isLoading = false;

    const loadSmart = async () => {
      if (
        document.visibilityState !== "visible" ||
        isLoading
      ) {
        return;
      }

      isLoading = true;

      await Promise.all([
        loadData(),
        loadRemboursements(),
        loadReservationsWeb()
      ]);

      isLoading = false;
    };

    loadSmart();

    const socket = io(
      process.env.REACT_APP_API_URL || window.location.origin,
      {
        transports: ["websocket", "polling"]
      }
    );

    socket.on("connect", () => {
      console.log("🟢 WebSocket connecté");
    });

    socket.on("remboursement_update", () => {
      loadRemboursements();
    });

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadSmart();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    );

    const handleFocus = () => loadSmart();

    window.addEventListener("focus", handleFocus);

    return () => {
      socket.disconnect();

      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      );

      window.removeEventListener(
        "focus",
        handleFocus
      );

    };
  }, []);

  // =====================
  // 📊 ANALYTICS
  // =====================

  const total = data.length;

  const occupes = data.filter(
    (a) => a.statut_reel === "occupe"
  ).length;

  const tauxOccupation =
    total > 0
      ? Math.round((occupes / total) * 100)
      : 0;

  const now = new Date();

  const currentMonth = data.filter((a) => {
    const d = new Date(a.date_debut);

    return d.getMonth() === now.getMonth();
  }).length;

  const lastMonth = data.filter((a) => {
    const d = new Date(a.date_debut);

    return d.getMonth() === now.getMonth() - 1;
  }).length;

  const last7Days = [...Array(7)]
    .map((_, i) => {
      const d = new Date();

      d.setDate(d.getDate() - i);

      const count = data.filter(
        (a) =>
          new Date(a.date_debut).toDateString() ===
          d.toDateString()
      ).length;

      return {
        date: d.toLocaleDateString("fr-FR", {
          weekday: "short"
        }),
        value: count
      };
    })
    .reverse();

  const compareData = [
    {
      name: "Mois passé",
      value: lastMonth
    },
    {
      name: "Mois courant",
      value: currentMonth
    }
  ];

  // =====================
  // 📋 LOGIC
  // =====================

  const arrivees = data.filter(
    (a) =>
      (
        a.statut_reel === "reserve" ||
        a.statut_reel === "confirme"
      ) &&
      isToday(a.date_debut)
  );

  const departs = data
    .filter(
      (a) =>
        (
          a.statut_reel === "occupe" ||
          a.statut_reel === "reserve" 
        ) &&
        isToday(a.date_fin_prevue)
    )
    .sort((a, b) => {
      if (
        a.reste_a_payer > 0 &&
        b.reste_a_payer === 0
      ) {
        return -1;
      }

      if (
        a.reste_a_payer === 0 &&
        b.reste_a_payer > 0
      ) {
        return 1;
      }

      return (
        b.reste_a_payer - a.reste_a_payer
      );
    });

  const urgences = data
    .filter(isUrgent)
    .sort(
      (a, b) =>
        b.reste_a_payer - a.reste_a_payer
    );

  const getGreeting = () => {
    const h = new Date().getHours();

    if (h < 12) return "Bon matin";

    if (h < 18) return "Bon après-midi";

    return "Bonsoir";
  };

  const greeting = getGreeting();

  const todayLabel = new Date().toLocaleDateString(
    "fr-FR",
    {
      weekday: "long",
      day: "numeric",
      month: "long"
    }
  );

  const nbArrivees = arrivees.length;

  const loadReservationsWeb = async () => {
    try {

      const res = await api.get("/sejours");

      console.log("SEJOURS API", res.data);

      const pending = res.data.filter(
        (s) =>
          s.source === "public" &&
          s.statut === "reserve"
      );

      setReservationsWeb(pending);

    } catch (err) {
      console.error(err);
    }
  };

  const confirmerReservation = async (id) => {
    try {

      await api.put(`/sejours/${id}/confirmer`);

      await loadReservationsWeb();
      await loadData();

    } catch (err) {

      console.error(err);

      alert(
        err.response?.data?.error ||
        "Erreur confirmation"
      );
    }
  };

  const activerNotifications = async () => {

    try {

      const permission =
        await Notification.requestPermission();

      console.log("PERMISSION =", permission);

      if (permission !== "granted") {

        alert(
          "Vous devez autoriser les notifications."
        );

        return;
      }

      const token = await getToken(
        messaging,
        {
          vapidKey:
            "BKomIh4GI_2rCWBR4gcM6VHV-7Cx6DYLBZH-r2o9dbkB5da890WpLNlC4lmEdkpJg1kkvvn3_SEPEp7jMSJWJDc"
        }
      );

      console.log(
        "FIREBASE TOKEN =",
        token
      );

      await api.post(
        "/notifications/register",
        {
          token
        }
      );

      setNotificationsEnabled(true);

      alert(
        "Notifications activées."
      );

    } catch (err) {

      console.error(
        "FIREBASE ERROR =",
        err
      );

      alert(
        err.response?.data?.error ||
        err.message ||
        "Erreur activation notifications."
      );

    }

  };

  useEffect(() => {

    const unsubscribe = onMessage(
      messaging,
      (payload) => {

        console.log(
          "NOTIFICATION RECUE",
          payload
        );

        new Notification(
          payload.notification.title,
          {
            body:
              payload.notification.body
          }
        );

      }
    );

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };

  }, []);

  return (
    <div className="p-3 md:p-6 bg-gray-100 min-h-[100dvh]">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">

        <div>
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">
            {greeting}

            <span className="block text-gray-500 text-base md:text-lg font-normal">
              Réceptionniste 👋
            </span>
          </h1>

          <p className="text-sm text-gray-500 mt-1 capitalize">
            {todayLabel}
          </p>
        </div>

        {!notificationsEnabled && (

          <button
            onClick={activerNotifications}
            className="
              px-4 py-2
              bg-blue-600
              text-white
              rounded-lg
              mb-4
            "
          >
            🔔 Activer les notifications
          </button>

        )}

        <div className="flex flex-col items-start md:items-end gap-2">

          <div className="bg-blue-50 text-blue-600 px-3 py-2 rounded-xl text-sm font-semibold shadow-sm">
            📊 Aujourd’hui : {nbArrivees} arrivée
            {nbArrivees > 1 ? "s" : ""}
          </div>

          {rembCount > 0 && (
            <div
              onClick={() =>
                navigate("/remboursements")
              }
              className="bg-orange-100 text-orange-700 px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer hover:bg-orange-200 transition"
            >
              🟠 {rembCount} remboursement
              {rembCount > 1 ? "s" : ""} en attente
            </div>
          )}

          <div className="text-sm text-gray-500">
            🔄 Temps réel
          </div>

        </div>

      </div>

      {/* 🌐 RÉSERVATIONS INTERNET */}

      {reservationsWeb.length > 0 && (

        <div className="bg-white rounded-2xl shadow p-4 mb-6">

          <div className="flex items-center justify-between mb-4">

            <h2 className="font-bold text-lg">
              🌐 Réservations Internet
            </h2>

            <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
              {reservationsWeb.length}
            </span>

          </div>

          <div className="space-y-3">

            {reservationsWeb.map((s) => (

              <div
                key={s.id}
                className="border rounded-xl p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
              >

                <div>

                  <div className="font-semibold">
                    {s.client_nom ||
                     `${s.prenom || ""} ${s.nom || ""}`.trim() ||
                     "Client"}
                  </div>

                  <div className="text-sm text-gray-500">
                    Appartement {s.appartement_numero}
                  </div>

                  <div className="text-sm text-gray-500">
                    {new Date(s.date_debut).toLocaleDateString("fr-FR")}
                    {" → "}
                    {new Date(
                      s.date_fin_prevue
                    ).toLocaleDateString("fr-FR")}
                  </div>

                </div>

                <button
                  onClick={() =>
                    confirmerReservation(s.id)
                  }
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl font-semibold"
                >
                  ✓ Confirmer
                </button>

              </div>

            ))}

          </div>

        </div>

      )}

      {/* LISTES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">

        {/* ARRIVÉES */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h2 className="font-bold text-base md:text-lg mb-3">
            Arrivées ({arrivees.length})
          </h2>

          <div className="max-h-[300px] overflow-y-auto">
            {arrivees.map((a) => {
              const status = getArrivalStatus();

              return (
                <div
                  key={a.id}
                  onClick={() => handleClick(a)}
                  className="py-2 cursor-pointer hover:bg-gray-100 px-2 rounded transition"
                >
                  🏢 {a.numero} -{" "}
                  {a.client || "Client"}

                  <div
                    className={`text-xs font-semibold ${status.color}`}
                  >
                    {status.label}
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* DÉPARTS */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h2 className="font-bold text-base md:text-lg mb-3">
            Départs ({departs.length})
          </h2>

          <div className="max-h-[300px] overflow-y-auto">
            {departs.map((a) => (
              <div
                key={a.id}
                onClick={() => handleClick(a)}
                className="py-2 cursor-pointer hover:bg-gray-100 px-2 rounded transition"
              >
                🏢 {a.numero}

                <div className="text-sm flex justify-between gap-2">

                  <span
                    className={
                      getDepartureStatus(a.date_fin_prevue)
                        .includes("dépassé")
                        ? "text-red-600"
                        : "text-orange-500"
                    }
                  >
                    {getDepartureStatus(a.date_fin_prevue)}
                  </span>

                </div>
              </div>
            ))}
          </div>

        </div>

        {/* URGENCES */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow border border-red-200">

          <h2 className="font-bold text-base md:text-lg text-red-600 mb-3">
            ⚠️ Urgences ({urgences.length})
          </h2>

          <div className="max-h-[300px] overflow-y-auto">
            {urgences.map((a) => {
              const isTodayUrgent =
                isUrgentToday(a);

              return (
                <div
                  key={a.id}
                  onClick={() => handleClick(a)}
                  className={`py-2 cursor-pointer px-2 rounded transition ${
                    isTodayUrgent
                      ? "text-red-700 bg-red-100 hover:bg-red-200"
                      : "text-orange-600 bg-orange-100 hover:bg-orange-200"
                  }`}
                >
                  🏢 {a.numero} - $
                  {a.reste_a_payer}

                  <span className="ml-2 text-xs font-bold">
                    {isTodayUrgent
                      ? "JOUR J"
                      : "J-1"}
                  </span>
                </div>
              );
            })}
          </div>

        </div>

      </div>

      {/* KPI + CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">

        {/* OCCUPATION */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="text-sm text-gray-500 mb-2">
            Taux d’occupation
          </h3>

          <div className="text-3xl md:text-4xl font-bold text-blue-600">
            {tauxOccupation}%
          </div>

        </div>

        {/* AFFLUENCE */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="text-sm text-gray-500 mb-2">
            Affluence (7 jours)
          </h3>

          <ResponsiveContainer
            width="100%"
            height={220}
          >
            <LineChart data={last7Days}>
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />

              <Line
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>

        </div>

        {/* COMPARATIF */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="text-sm text-gray-500 mb-2">
            Comparatif mensuel
          </h3>

          <ResponsiveContainer
            width="100%"
            height={220}
          >
            <BarChart data={compareData}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="name" />

              <YAxis allowDecimals={false} />

              <Tooltip />

              <Bar
                dataKey="value"
                fill="#10b981"
              />
            </BarChart>
          </ResponsiveContainer>

        </div>

      </div>

    </div>
  );
}